import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, Building2, Clock, Calendar, 
  CheckCircle2, TrendingUp, Code, Layers, ExternalLink
} from 'lucide-react';
import { applicationsService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function ApplicationDetailPage() {
  const { slug } = useParams();
  const [application, setApplication] = useState(null);
  const [otherApps, setOtherApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadApplication();
  }, [slug]);

  const loadApplication = async () => {
    try {
      const response = await applicationsService.getBySlug(slug);
      setApplication(response.data.application);
      setOtherApps(response.data.otherApplications || []);
    } catch (error) {
      console.error('Erreur:', error);
      setError('Application non trouvée');
    } finally {
      setLoading(false);
    }
  };

  const formatPeriod = () => {
    if (!application.period_start) return null;
    
    const start = format(new Date(application.period_start), 'MMM yyyy', { locale: fr });
    const end = application.period_end 
      ? format(new Date(application.period_end), 'MMM yyyy', { locale: fr })
      : 'En cours';
    
    return `${start} - ${end}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Chargement..." />
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-dark-900 mb-4">Application non trouvée</h1>
          <Link to="/nos-applications" className="btn-primary">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour aux applications
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero avec image de fond */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0">
          {application.hero_image_url || application.image_url ? (
            <img 
              src={application.hero_image_url || application.image_url}
              alt={application.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary-700 to-dark-900" />
          )}
          <div className="absolute inset-0 bg-dark-900/60" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            {application.category && (
              <span className="inline-block px-4 py-1 bg-dark-700 text-white text-sm rounded-full mb-4">
                {application.category}
              </span>
            )}
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {application.title}
            </h1>
            
            <p className="text-lg text-gray-300">
              {application.short_description}
            </p>

            {/* Bouton Démo dans le Hero si URL existe */}
            {application.demo_url && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-8"
              >
                <a 
                  href={application.demo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-medium hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-xl"
                >
                  <span> Essayer la démo gratuite</span>
                  <ExternalLink className="w-5 h-5" />
                </a>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Infos rapides */}
      <section className="bg-white border-b border-dark-100">
        <div className="container-custom py-6">
          <div className="flex flex-wrap justify-center gap-12 md:gap-24">
            {application.client_name && (
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-dark-400" />
                <div>
                  <p className="text-xs text-dark-400 uppercase tracking-wider">Client</p>
                  <p className="font-medium text-dark-900">{application.client_name}</p>
                </div>
              </div>
            )}
            
            {application.duration && (
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-dark-400" />
                <div>
                  <p className="text-xs text-dark-400 uppercase tracking-wider">Durée</p>
                  <p className="font-medium text-dark-900">{application.duration}</p>
                </div>
              </div>
            )}
            
            {formatPeriod() && (
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-dark-400" />
                <div>
                  <p className="text-xs text-dark-400 uppercase tracking-wider">Période</p>
                  <p className="font-medium text-dark-900">{formatPeriod()}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contenu principal */}
      <section className="section bg-dark-50 py-16">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Colonne principale */}
            <div className="lg:col-span-2 space-y-6">
              {/* Vue d'ensemble */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-8 border border-dark-100"
              >
                <h2 className="text-2xl font-bold text-dark-900 mb-6">Vue d'ensemble</h2>
                <div className="text-dark-600 leading-relaxed whitespace-pre-line">
                  {application.description}
                </div>

                {application.benefits && application.benefits.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-dark-100">
                    <h3 className="font-semibold text-dark-900 mb-4">Bénéfices</h3>
                    <ul className="space-y-2">
                      {application.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2 text-dark-600">
                          <CheckCircle2 className="w-5 h-5 text-secondary-500 mt-0.5 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>

              {/* Technologies utilisées */}
              {application.technologies && application.technologies.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl p-8 border border-dark-100"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <Code className="w-5 h-5 text-dark-700" />
                    <h2 className="text-xl font-bold text-dark-900">Technologies Utilisées</h2>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {application.technologies.map((tech, index) => (
                      <span 
                        key={index}
                        className="px-4 py-2 border border-dark-200 rounded-full text-dark-700 text-sm"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Résultats & Impact */}
              {application.results && application.results.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="rounded-2xl p-6 text-white"
                  style={{ backgroundColor: 'rgb(26, 50, 84)' }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <TrendingUp className="w-5 h-5" />
                    <h3 className="text-lg font-bold">Résultats & Impact</h3>
                  </div>
                  <ul className="space-y-3">
                    {application.results.map((result, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-200 text-sm">{result}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}

              {/* CTA Devis */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl p-6 border border-dark-100"
              >
                <h3 className="text-lg font-semibold text-dark-900 mb-2">
                  Intéressé par cette solution ?
                </h3>
                <p className="text-dark-500 text-sm mb-4">
                  Contactez-nous pour discuter de vos besoins et obtenir un devis personnalisé.
                </p>
                <Link 
                  to="/contact" 
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 text-white rounded-lg font-medium transition-colors"
                  style={{ backgroundColor: 'rgb(26, 50, 84)' }}
                >
                  Demande de devis
                  <ArrowRight className="w-5 h-5" />
                </Link>
                
                {/* Bouton Démo - Affiché uniquement si demo_url existe */}
                {application.demo_url && (
                  <div className="mt-4 pt-4 border-t border-dark-100">
                    <p className="text-dark-600 text-sm mb-3 text-center">
                       Envie de découvrir l'application en action ?
                    </p>
                    <a 
                      href={application.demo_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg"
                    >
                      <span>Essayer la démo gratuite</span>
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  </div>
                )}
              </motion.div>

              <Link 
                to="/nos-applications"
                className="flex items-center gap-2 text-dark-500 hover:text-primary-600 transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour aux applications
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Autres applications */}
      {otherApps.length > 0 && (
        <section className="section bg-white py-16">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-dark-900">
                Découvrez nos autres applications
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherApps.slice(0, 3).map((app, index) => (
                <motion.article
                  key={app.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link 
                    to={`/nos-applications/${app.slug}`}
                    className="block h-full group"
                  >
                    <div className="bg-white rounded-2xl overflow-hidden border border-dark-100 hover:shadow-lg transition-shadow h-full">
                      <div className="aspect-video bg-dark-100 relative overflow-hidden">
                        {app.image_url ? (
                          <img 
                            src={app.image_url} 
                            alt={app.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-dark-200 to-dark-300">
                            <Layers className="w-12 h-12 text-dark-400" />
                          </div>
                        )}
                      </div>
                      
                      <div className="p-6">
                        <h3 className="text-lg font-semibold text-dark-900 mb-2 group-hover:text-primary-600 transition-colors">
                          {app.title}
                        </h3>
                        <p className="text-dark-500 text-sm line-clamp-2">
                          {app.short_description}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default ApplicationDetailPage;